import { useState, useEffect } from "react";
import {
  Divider,
  Typography,
  Drawer,
  Dropdown,
  Menu,
  Button,
  Badge,
  Avatar,
  Space,
  Image,
} from "antd";
import Slider from "react-slick";
import {
  EnvironmentOutlined,
  DownOutlined,
  MenuOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  GiftOutlined,
  TableOutlined,
  HomeOutlined,
  CloseOutlined,
  ArrowLeftOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import "./index.css";
import { mstGeneral } from "../../mobx";
import { observer } from "mobx-react-lite";
import { getSnapshot } from "mobx-state-tree";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../cart/CartContext";
import { useAuth } from "../auth/AuthContext";
import { useReferral } from "../../utils/referral";

const { Text, Link } = Typography;

const Navbar = observer((props: any) => {
  const location = useLocation();
  const { hideBanner } = props;
  const { isLoggedIn, onOpenLoginModal, logout, user } = useAuth();
  const { showCart, items } = useCart();
  const [visible, setVisible] = useState(false);
  const [imageSlides, setImageSlides] = useState([]);
  const { referralCode, navigateWithReferral } = useReferral();

  useEffect(() => {
    mstGeneral.fetchCompanyDetails();
    mstGeneral
      .fetchImageSlides()
      .then((response) => {
        setImageSlides(response);
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const toggleDrawer = () => {
    setVisible(!visible);
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    arrows: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
  };

  const userMenu = (
    <Menu>
      <Menu.Item
        key="profile"
        icon={
          <UserOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
      >
        <a target="_self" href="/profile/edit">
          My Profile
        </a>
      </Menu.Item>
      <Menu.Item
        key="bookings"
        icon={
          <HomeOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
      >
        <a target="_self" href="/bookings/room">
          My Bookings
        </a>
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        key="getHelp"
        onClick={() => {
          window.open("https://api.whatsapp.com/send?phone=60102621263");
        }}
      >
        Get Help
      </Menu.Item>
      <Menu.Item key="logout" onClick={() => logout()} danger>
        Logout
      </Menu.Item>
    </Menu>
  );

  const mobileMenu = (
    <Menu>
      <Menu.Item
        key="room"
        icon={
          <HomeOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
        onClick={() => {
          navigateWithReferral("/room");
          setVisible(false);
        }}
      >
        Rooms & Suites
      </Menu.Item>
      <Menu.Item
        key="table"
        icon={
          <TableOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
        onClick={() => {
          navigateWithReferral("/table");
          setVisible(false);
        }}
      >
        Tables
      </Menu.Item>
      <Menu.Item
        key="voucher"
        icon={
          <GiftOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
        onClick={() => {
          navigateWithReferral("/voucher");
          setVisible(false);
        }}
      >
        Vouchers
      </Menu.Item>
      <Menu.Item
        key="product"
        icon={
          <ShoppingCartOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />
        }
        onClick={() => {
          navigateWithReferral("/product");
          setVisible(false);
        }}
      >
        Menu
      </Menu.Item>
      <Menu.Divider />
      {isLoggedIn ? (
        <>
          <Menu.Item
            key="profile-mobile"
            onClick={() => {
              navigateWithReferral("/profile/edit");
              setVisible(false);
            }}
          >
            My Profile
          </Menu.Item>
          <Menu.Item
            key="bookings-mobile"
            onClick={() => {
              navigateWithReferral("/bookings/room");
              setVisible(false);
            }}
          >
            My Bookings
          </Menu.Item>
          <Menu.Item
            key="logout-mobile"
            onClick={() => {
              logout();
              setVisible(false);
            }}
            danger
          >
            Logout
          </Menu.Item>
        </>
      ) : (
        <Menu.Item
          key="login-mobile"
          onClick={() => {
            onOpenLoginModal();
            setVisible(false);
          }}
        >
          Login
        </Menu.Item>
      )}
    </Menu>
  );

  // Enhanced Mobile Drawer Content
  const MobileDrawerContent = () => {
    const currentPath = location.pathname;
    const navigate = useNavigate();

    return (
      <div className="mobile-drawer">
        {/* Back to Website Link */}
        <div className="mobile-website-link">
          <a href="/" target="_self" className="website-link-mobile">
            <ArrowLeftOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            Back to Main Website
          </a>
        </div>

        {/* User Section */}
        <div className="mobile-user-section">
          {isLoggedIn ? (
            <div className="user-info">
              <Avatar
                size="large"
                icon={
                  <UserOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
                src={user?.avatar}
                style={{
                  background: "linear-gradient(135deg, #e90056, #ff6b6b)",
                  flexShrink: 0,
                }}
              />
              <div className="user-details">
                <div className="user-name">
                  {user?.firstname} {user?.lastname}
                </div>
                <div className="user-email">{user?.email}</div>
              </div>
            </div>
          ) : (
            <Button
              type="primary"
              onClick={() => {
                onOpenLoginModal();
                setVisible(false);
              }}
              className="login-btn-mobile"
              icon={
                <UserOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
            >
              Sign In to Your Account
            </Button>
          )}
        </div>

        {/* Main Navigation Section */}
        <div className="mobile-nav-section">
          <div className="nav-section-title">Main Menu</div>

          <button
            className={`mobile-menu-item ${
              currentPath.includes("/room") ? "active" : ""
            }`}
            onClick={() => {
              navigate("/room");
              setVisible(false);
            }}
          >
            <HomeOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            <span>Rooms & Suites</span>
          </button>

          {/* Dining & Promotions Parent */}
          {/* <div
            className={`mobile-menu-group ${
              currentPath.includes("/table") ||
              currentPath.includes("/voucher") ||
              currentPath.includes("/product")
                ? "active"
                : ""
            }`}
          >
            <div className="mobile-menu-group-header">
              <AppstoreOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
              <span>Dining & Promotions</span>
            </div>
            <div className="mobile-menu-group-items">
              <button
                className={`mobile-menu-item sub-item ${
                  currentPath.includes("/table") ? "active" : ""
                }`}
                onClick={() => {
                  navigate("/table");
                  setVisible(false);
                }}
              >
                <TableOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <span>Tables</span>
              </button>

              <button
                className={`mobile-menu-item sub-item ${
                  currentPath.includes("/voucher") ? "active" : ""
                }`}
                onClick={() => {
                  navigate("/voucher");
                  setVisible(false);
                }}
              >
                <GiftOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <span>Vouchers</span>
              </button>

              <button
                className={`mobile-menu-item sub-item ${
                  currentPath.includes("/product") ? "active" : ""
                }`}
                onClick={() => {
                  navigate("/product");
                  setVisible(false);
                }}
              >
                <ShoppingCartOutlined
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
                <span>Menu</span>
              </button>
            </div>
          </div> */}
        </div>

        {/* User Account Section - Only show if logged in */}
        {isLoggedIn && (
          <div className="mobile-nav-section">
            <Divider className="nav-divider" />
            <div className="nav-section-title">My Account</div>

            <button
              className={`mobile-menu-item ${
                currentPath.includes("/profile/edit") ? "active" : ""
              }`}
              onClick={() => {
                navigate("/profile/edit");
                setVisible(false);
              }}
            >
              <UserOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
              <span>My Profile</span>
            </button>

            <button
              className={`mobile-menu-item ${
                currentPath.includes("/bookings") ? "active" : ""
              }`}
              onClick={() => {
                navigate("/bookings/room");
                setVisible(false);
              }}
            >
              <HomeOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
              <span>My Bookings</span>
            </button>

            <button
              className="mobile-menu-item logout"
              onClick={() => {
                logout();
                setVisible(false);
              }}
            >
              <CloseOutlined
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
              <span>Logout</span>
            </button>
          </div>
        )}
      </div>
    );
  };

  const cartItemsCount = items?.length || 0;

  return (
    <div id="NavbarLayout">
      {/* Referral Notification Bar */}
      {referralCode && (
        <div className="referral-notification-bar">
          <div className="container">
            <div className="referral-message">
              🎉 Your referrer is <strong>{referralCode}</strong>
            </div>
          </div>
        </div>
      )}

      {/* Top Bar - Desktop Only Back to Website Link */}
      <div className="top-bar">
        <div className="container">
          {/* Show on desktop, hide on mobile */}
          <Link href="/" className="return-link " target="_self">
            ← Main Website
          </Link>
          <div className="top-bar-actions">
            <EnvironmentOutlined
              className="location-icon"
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            <Text className="location-text">
              {getSnapshot(mstGeneral)?.companyDetails?.Hotel_Address}
            </Text>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="main-nav" style={{ marginBottom: hideBanner ? 20 : 0 }}>
        <div className="container">
          {/* Logo and Brand - Only Image */}
          <div className="brand-section">
            <div className="logo-wrapper">
              <Image
                src="https://www.primulahotels.com/img/logo.png"
                alt="Primula Hotel"
                preview={false}
                className="hotel-logo"
                width="auto"
                height="auto"
              />
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="nav-desktop">
            <div className="nav-links">
              <Button
                type="text"
                icon={
                  <HomeOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
                onClick={() => navigateWithReferral("/room")}
                className={`nav-link ${
                  location.pathname.includes("/room") ? "active" : ""
                }`}
              >
                Rooms & Suites
              </Button>
              {/* @ts-ignore */}
              {/* <Dropdown
                overlay={
                  <Menu>
                    <Menu.Item
                      key="table"
                      icon={
                        <TableOutlined
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      }
                      onClick={() => navigateWithReferral("/table")}
                    >
                      Tables
                    </Menu.Item>
                    <Menu.Item
                      key="voucher"
                      icon={
                        <GiftOutlined
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      }
                      onClick={() => navigateWithReferral("/voucher")}
                    >
                      Vouchers
                    </Menu.Item>
                    <Menu.Item
                      key="product"
                      icon={
                        <ShoppingCartOutlined
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      }
                      onClick={() => navigateWithReferral("/product")}
                    >
                      Menu
                    </Menu.Item>
                  </Menu>
                }
                placement="bottomLeft"
              >
                <Button
                  type="text"
                  icon={
                    <AppstoreOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  }
                  className={`nav-link ${
                    location.pathname.includes("/table") ||
                    location.pathname.includes("/voucher") ||
                    location.pathname.includes("/product")
                      ? "active"
                      : ""
                  }`}
                >
                  Dining & Promotions
                  <DownOutlined
                    style={{ fontSize: "10px", marginLeft: "4px" }}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </Button>
              </Dropdown> */}
            </div>

            {/* User Actions Section */}
            <div className="user-actions">
              {/* @ts-ignore */}
              {/* <Badge count={cartItemsCount} size="small" className="cart-badge">
                <Button
                  type="default"
                  icon={
                    <ShoppingCartOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  }
                  onClick={() => showCart(location.pathname)}
                  className="cart-action-btn"
                >
                  Cart
                </Button>
              </Badge> */}

              {isLoggedIn ? (
                // @ts-ignore
                <Dropdown overlay={userMenu} placement="bottomRight">
                  <Button type="default" className="user-action-btn">
                    <Space>
                      <Avatar
                        size="small"
                        icon={
                          <UserOutlined
                            onPointerEnterCapture={undefined}
                            onPointerLeaveCapture={undefined}
                          />
                        }
                        src={user?.avatar}
                        className="user-avatar"
                      />
                      <span className="user-display-name">
                        {user?.firstname}
                      </span>
                      <DownOutlined
                        className="dropdown-arrow"
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    </Space>
                  </Button>
                </Dropdown>
              ) : (
                <Button
                  type="primary"
                  onClick={onOpenLoginModal}
                  className="login-action-btn"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="nav-mobile">
            <div className="mobile-actions">
              {/* @ts-ignore */}
              {/* <Badge count={cartItemsCount} size="small" className="cart-badge">
                <Button
                  type="default"
                  icon={
                    <ShoppingCartOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  }
                  onClick={() => showCart(location.pathname)}
                  className="cart-action-btn-mobile"
                />
              </Badge> */}

              {!isLoggedIn && (
                <Button
                  type="primary"
                  onClick={onOpenLoginModal}
                  className="login-action-btn-mobile"
                >
                  Sign In
                </Button>
              )}

              <Button
                type="text"
                icon={
                  <MenuOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                }
                onClick={toggleDrawer}
                className="menu-btn"
              />
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      {/* @ts-ignore */}
      <Drawer
        title={
          <div
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#1a1a1a",
            }}
          >
            Menu
          </div>
        }
        placement="right"
        onClose={toggleDrawer}
        visible={visible}
        width={300}
        closable={true}
        styles={{
          body: { padding: 0 },
          header: {
            padding: "16px 20px",
            borderBottom: "1px solid #f0f0f0",
          },
        }}
      >
        <MobileDrawerContent />
      </Drawer>

      {/* Banner Slider */}
      <div
        style={{ display: hideBanner ? "none" : "block" }}
        className="sliderWrapper"
      >
        {imageSlides.length > 0 ? (
          <Slider {...settings}>
            {imageSlides.map((slide: any, slideIndex) => {
              return (
                <div key={slide.Image_Id} className="slide-item">
                  <img
                    src={slide.Image_Url}
                    alt={slide.Image_Description}
                    className="slide-image"
                  />
                  {slide.Image_Description && (
                    <div className="slide-caption">
                      <Text className="caption-text">
                        {slide.Image_Description}
                      </Text>
                    </div>
                  )}
                </div>
              );
            })}
          </Slider>
        ) : null}
      </div>
    </div>
  );
});

export default Navbar;
